import { EvaluationRatingAggregationQuery } from '@innovation-os-ng/common';
import { IqlQuery } from '@itonics/filters';
import { FactSummary } from '../../facts/interfaces/fact';
export { FactSummary } from '../../facts/interfaces/fact';

export interface Document {
  /**
   * Document id
   */
  uri: string;

  /**
   * Type of the document
   */
  type: string;

  /**
   * Title of the document
   */
  label: string;

  /**
   * Summary of the document
   */
  summary: string;

  /**
   * Description of the document, rich text field
   */
  description?: string;

  /**
   * URL of the document
   */
  url?: string;

  /**
   * Source of the document
   * Basically it's the base domain of the URL or USPTO/EPO for patents
   */
  source?: string;

  /**
   * Language of the document
   */
  language?: string;

  /**
   * Date when the document was published
   */
  publishedDate?: number;

  /**
   * country specified in document
   */
  country?: string;
  imageUrl?: string;

  /**
   * ************************************
   * RSS feed specific properties
   * ************************************
   */

  /**
   * Authors name specified in RSS/Atom feed
   */
  authors?: Array<string>;

  /**
   * Categories specified in RSS/Atom feed
   * can have multiple values
   */
  categories?: Array<string>;

  /**
   * ************************************
   * Patent specific properties
   * ************************************
   */

  /**
   * Patent brief description, rich text field
   */
  patentBriefDescription?: string;

  /**
   * Patent priority dates, can be multiple
   */
  patentPriorityDates?: Array<number>;

  /**
   * Patent applicants
   */
  patentApplicants?: Array<string>;
  patentApplicant?: Array<ApplicantWithAddress>;
  /**
   * Patent inventors
   */
  patentInventors?: Array<string>;
  patentInventor?: Array<ApplicantWithAddress>;
  /**
   * Patent assignees
   */
  patentAssignees?: Array<string>;
  patentAssignee?: Array<ApplicantWithAddress>;
  /**
   * Patent claims
   */
  patentClaims?: Array<string>;
  patentKind?: string;
  entities?: {
    [key: string]: Array<string> | Array<EnrichedEntity>;
  };

  entitiesList?: Array<FactSummary>;

  /**
   * Metadata of the document
   * Generally its key-value pairs,
   * specifying details about the source of the document and other details
   */
  _metadata: { [key: string]: any };

  createdOn?: number;
  updatedOn?: number;
  patentPriorityClaims?: Array<PriorityClaim>;

  /**
   * A list of enriched Topics
   */
  subType?: string;
  topics?: Array<Topic>;
  iabCategories?: Array<Category>;
  iptcCategories?: Array<Category>;
  doi?: string;
}

// Document, extended with display-relevant properties
export interface FeedDocument extends Document {
  removedByUser?: boolean;
}

export interface EnrichedEntity {
  label: string;
  score: string;
}

interface ApplicantWithAddress {
  name: string; // person name or organization name
  street?: string;
  city?: string;
  state?: string;
  country: string;
}

interface PriorityClaim {
  docNumber: string;
  date: number;
  country: string;
}

export interface Category {
  score?: number;
  id?: string;
  label?: string;
}

export interface Topic {
  /**
   * Label from TextRazor topics
   */
  label: string;
  /**
   * WikiDataId from TextRazor topics
   */
  wikidataId?: string;
  /**
   * Score from TextRazor topics
   */
  score?: number;
  /**
   * WikiLink from TextRazor topics
   */
  wikiLink?: string;
}

export interface SortOption {
  sortBy: string;
  sortOrder: string;
}

export const FEED_TYPES: Array<string> = ['News', 'Patent', 'RSS'];

export interface ActionPerformedDocument {
  uri: string;
  performedDate?: number;
  performedBy?: {
    firstName: string;
    lastName: string;
  };
}

export interface ActionTriggered {
  actionButtonID: string;
  documentUri: string;
  detail: Document;
  action: string;
  value?: boolean;
}

export interface Action {
  id: string;
  label?: string;
  icon: string;
  activeIcon: string;
  title: string;
  actionEvents: Array<ActionEvent>;
  customClass?: string;
  stickToTop?: boolean;
  isReadonly?: boolean;
  showAsFilter?: boolean;
  callbackFn?: () => void;
  actionPerformedDocuments?: Array<ActionPerformedDocument>;
  tooltip: {
    active: string;
    inactive: string;
    readonly: string;
  };
  evaluationBased?: boolean; // Whether this action affects evaluations (eg. rating, bad-signal)
}

export enum ActionEvent {
  CLICK = 'click',
  MOUSE_ENTER = 'mouseenter',
  MOUSE_Leave = 'mouseleave'
}

export enum ActionId {
  BAD_SIGNAL = 'action-bad-signal'
}

export interface FilterQuery {
  aggregations?: Array<string>;
  sortOrder?: string;
  sortBy?: string;
  highlight?: boolean;
  limit?: number;
  query?: IqlQuery;
  queryRawDocuments?: boolean;
  searchFieldUri?: string;
  paginationCursor?: string;
  fields?: Array<string>;
  criterionUri?: string;
  criterionValue?: string;
  minRatingCountForFiltration?: number;
  queryDuplicateDocuments?: boolean;
}

export interface AggregationCount {
  key: string;
  doc_count: number;
}

export interface PinnedContainer {
  signals?: Array<string>;
}

export interface SearchField {
  uri: string;
  state?: SearchFieldState; // Implicit/Refined search fields are linked to an element, Explicit is independent
  elementUri?: string; // URI of the linked element (should be null for state: 'explicit')
  title: string;
  query: IqlQuery;
  associatedElements: Array<string>; // an array of URIs
  documentTypes?: Array<string>;
  publishedDate?: {
    startDate: number;
    endDate: number;
    quickSelection?: string;
  };
  spaceUri?: string;
  pinned?: PinnedContainer;
  relatesTo?: string;
}
export interface DocumentColorMap {
  [key: string]: string;
}

export enum SearchFieldIdentifiers {
  CASUAL = 'cu',
  CONTEXT = 'co',
  POWER = 'pu',
  COMBINED = 'cb',
  SEARCH_FIELD = 'sf',
  ELEMENT = 'el',
  SELECTION = 'sl'
}

export enum ContextQueryIdentifier {
  PUBLISHED_DATE = 'publishedDate',
  DOCUMENT_TYPE = 'type'
}

export enum SearchFieldState {
  IMPLICIT = 'implicit', // Default searchField linked to an element
  REFINED = 'refined', // Modified searchField linked to an element
  EXPLICIT = 'explicit' // Unlinked searchField
}

export interface SearchFieldQueryParams {
  limit?: number;
  paginationCursor?: string;
  associatedElements?: string;
  title?: string;
  elementUri?: string;
  state?: SearchFieldState;
  relatesTo?: string;
}

export enum SignalFilterOptionType {
  IMPACT = 'impact',
  ACTION = 'action'
}

export enum EndpointType {
  TOP_RATED = 'topN',
  USER_RATED = 'ratedDocuments'
}

export interface SignalFilterOption {
  label: string;
  key: string;
  disabled?: boolean;
  loadValueByEndpoint?: boolean;
  value?: number | string;
  type: SignalFilterOptionType;
  includeEvaluationBasis?: boolean;
  endpointType?: EndpointType;
  clearSearchFilter?: boolean; // If this is true we need to clear previous filters
  featureFlag?: string; // Option can be hidden by disabling this feature flag
  criterionUri?: string;
  criterionValue?: string;
  filterBy?: EvaluationRatingAggregationQuery; // Replaces criterionUri/criterionValue in the filtered-documents query,
  emptyResultsTips?: Array<string>; // Translatable keys for text to display when there are no results for this option
  showCount?: boolean; // Should we fetch a count of this option to show in the dropdown
  showBlockedSignalAnimation?: boolean; // Should we show the animation when (un)blocking a signal
}

export enum SignalFilterOptionKey {
  BAD_SIGNAL = 'BAD_SIGNAL',
  NEGATIVE_IMPACT = 'NEGATIVE_IMPACT',
  POSITIVE_IMPACT = 'POSITIVE_IMPACT',
  LESS_RELEVANT_SIGNAL = 'LESS_RELEVANT_SIGNAL',
  ALL_RELEVANT = 'allRelevant'
}

export interface FeedEvaluationBasis {
  evaluationBasis: EvaluationBasis;
  uri: string;
}

export enum FeedPart {
  TOPIC = 'topic',
  Entity = 'entity',
  CATEGORY = 'category',
  SOURCE = 'source',
  COUNTRY = 'country'
}

export enum EvaluationBasis {
  ENTITY = 'entityUri',
  SEARCH_FIELD = 'searchFieldUri'
}

export interface FeedPartDetail {
  type: FeedPart;
  uri: string;
  group?: string;
  label?: string;
}
export interface SearchTip {
  tip: string;
}

// Uri of the field (on an element) where we save the signal it was created from
export const SIGNALS_SOURCE_URI = 'signalsSource';

// Uri of the field (on an element) where we save the fact it was created from
export declare const ENTITY_SOURCE_URI = 'entitySource';
